import { Container, Graphics } from 'pixi.js'
import { FIELD_RADIUS } from '@star-snake/shared'
import { NeonColorsHex } from '../ui/neon-theme'

/** Nebula color palette: deep purple, teal, dark blue */
const NEBULA_COLORS = [0x1a0033, 0x001a2e, 0x0d1a33, 0x0a0d2e, 0x001a1a]

/** Star color tints: white, slight blue, slight purple */
const STAR_COLORS = [0xffffff, 0xccddff, 0xddccff, 0xffffff, 0xccccff]

export class BackgroundRenderer {
  private container: Container
  private starsGraphics: Graphics
  private nebulaGraphics: Graphics
  private boundaryGraphics: Graphics
  private fieldRadius: number

  constructor(parentContainer: Container, fieldRadius: number = FIELD_RADIUS) {
    this.fieldRadius = fieldRadius
    this.container = new Container()
    parentContainer.addChildAt(this.container, 0)

    this.nebulaGraphics = new Graphics()
    this.starsGraphics = new Graphics()
    this.boundaryGraphics = new Graphics()

    this.container.addChild(this.nebulaGraphics)
    this.container.addChild(this.starsGraphics)
    this.container.addChild(this.boundaryGraphics)

    this.drawNebulae()
    this.drawStars()
    this.drawBoundary()
  }

  /** Static star field with varied sizes, brightness, and color tints */
  private drawStars(): void {
    const gfx = this.starsGraphics
    // Scale star count with map size, cap at 2000
    const starCount = Math.min(2000, 200 + Math.floor(this.fieldRadius / 20))

    for (let i = 0; i < starCount; i++) {
      const angle = Math.random() * Math.PI * 2
      const r = this.fieldRadius * 1.15 * Math.sqrt(Math.random())
      const x = r * Math.cos(angle)
      const z = r * Math.sin(angle)
      const size = 1 + Math.random() * 2
      const alpha = 0.3 + Math.random() * 0.6
      const color = STAR_COLORS[i % STAR_COLORS.length]

      gfx.circle(x, z, size)
      gfx.fill({ color, alpha })
    }
  }

  /** Soft nebula blobs at random positions within 70% of field radius */
  private drawNebulae(): void {
    const gfx = this.nebulaGraphics
    // Scale nebula count with map size
    const count = 5 + Math.floor(Math.random() * 5)

    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2
      const dist = this.fieldRadius * 0.7 * Math.random()
      const cx = dist * Math.cos(angle)
      const cz = dist * Math.sin(angle)
      // Scale nebula radius with map
      const radius = (300 + Math.random() * 600) * (this.fieldRadius / 10000)
      const alpha = 0.05 + Math.random() * 0.07
      const color = NEBULA_COLORS[i % NEBULA_COLORS.length]

      gfx.circle(cx, cz, radius)
      gfx.fill({ color, alpha })
    }
  }

  /** Bright neon boundary line — static death wall (slither.io style).
   * Stroke widths in world units (not pixels) so they're visible at any zoom. */
  private drawBoundary(): void {
    const gfx = this.boundaryGraphics
    const color = 0xff0000 // Red death wall
    const warningColor = 0xff3333

    // Outermost danger glow (wide, faint red)
    gfx.circle(0, 0, this.fieldRadius)
    gfx.stroke({ width: 200, color, alpha: 0.08 })

    // Middle warning glow
    gfx.circle(0, 0, this.fieldRadius)
    gfx.stroke({ width: 80, color: warningColor, alpha: 0.2 })

    // Inner bright core (clearly visible)
    gfx.circle(0, 0, this.fieldRadius)
    gfx.stroke({ width: 30, color, alpha: 0.9 })
  }

  setFieldRadius(radius: number): void {
    if (!Number.isFinite(radius) || radius <= 0) return
    if (Math.abs(radius - this.fieldRadius) < 0.1) return
    this.fieldRadius = radius
    this.boundaryGraphics.clear()
    this.drawBoundary()
  }

  dispose(): void {
    this.starsGraphics.clear()
    this.nebulaGraphics.clear()
    this.boundaryGraphics.clear()
    this.starsGraphics.destroy()
    this.nebulaGraphics.destroy()
    this.boundaryGraphics.destroy()
    this.container.removeChildren()
    this.container.parent?.removeChild(this.container)
    this.container.destroy({ children: true })
  }
}
